import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../../services/api";
import {
    Typography,
    Spin,
    Button,
    message,
    Empty,
    Tag,
    Space,
    Popconfirm,
    Pagination,
    Select,
    Modal,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    EyeOutlined,
    EnvironmentOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import "./MyAds.css";

const { Text, Paragraph } = Typography;

function MyAds() {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [filter, setFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({
        total: 0,
        activeCount: 0,
        pendingCount: 0,
        totalValue: 0,
    });

    const [isMobileFilter, setIsMobileFilter] = useState(
        typeof window !== "undefined" ? window.innerWidth <= 460 : false
    );

    const navigate = useNavigate();
    const updatingRef = useRef(false);

    const fetchAds = useCallback(async (pageNumber = 1) => {
        if (updatingRef.current) return;

        try {
            updatingRef.current = true;
            setLoading(true);

            const params = { page: pageNumber, limit: 15 };

            const res = await API.get(
                `/ads/my/listings?page=${pageNumber}&filter=${filter}`
            );

            setAds(res.data.ads || []);
            setTotal(res.data.total || 0);
            setStats(res.data.stats || {});
        } catch {
            message.error("Failed to load ads");
        } finally {
            updatingRef.current = false;
            setLoading(false);

        }
    }, [filter]);

    useEffect(() => {
        setPage(1);
        fetchAds(1);
    }, [filter]);

    useEffect(() => {
        fetchAds(page);
    }, [page]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileFilter(window.innerWidth <= 460);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleStatusToggle = async (id, status) => {
        try {
            setActionLoadingId(id);
            const endpoint = status === "active" ? "/deactivate" : "/activate";
            await API.put(`/ads/${id}${endpoint}`);
            message.success("Updated");
            await fetchAds(page);
        } catch {
            message.error("Failed");
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDelete = (id, title) => {
        Modal.confirm({
            title: "Delete Listing",
            content: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
            okText: "Delete",
            okButtonProps: { danger: true },
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    await API.delete(`/ads/${id}`);
                    message.success("Listing deleted successfully");
                    setAds((prev) => prev.filter((a) => a._id !== id));
                } catch {
                    message.error("Failed to delete listing");
                }
            },
        });
    };



    const getStatusConfig = (status) =>
        ({
            active: { color: "#10b981", bg: "#ecfdf5", label: "Active" },
            inactive: { color: "#6b7280", bg: "#f3f4f6", label: "Inactive" },
        })[status] || { color: "#ef4444", bg: "#fef2f2", label: "Error" };

    const getApprovalConfig = (status) =>
        ({
            pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
            approved: { color: "#3b82f6", bg: "#eff6ff", label: "Approved" },
            rejected: { color: "#ef4444", bg: "#fef2f2", label: "Rejected" },
        })[status] || { color: "#6b7280", bg: "#f3f4f6", label: "Unknown" };
    if (loading) {
        return (
            <div className="ads-page">
                <div className="ads-shell">
                    <div className="ads-loading">
                        <Spin size="large" />
                    </div>
                </div>
            </div>
        );
    }

    const activeCount = stats.activeCount;
    const pendingCount = stats.pendingCount;
    const totalValue = stats.totalValue || 0;

    const filterOptions = [
        { label: "All", value: "all" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
    ];

    return (
        <div className="ads-page">
            <div className="ads-shell">
                {/* ── OVERVIEW ── */}
                <section className="ads-overview">
                    <div className="ads-overview__stats">
                        <div className="ads-overview__stat">
                            <div className="ads-overview__value">{stats.total}</div>
                            <div className="ads-overview__label">Total Ads</div>
                        </div>

                        <div className="ads-overview__stat">
                            <div className="ads-overview__value ads-overview__value--active">
                                {activeCount}
                            </div>
                            <div className="ads-overview__label">Active</div>
                        </div>

                        <div className="ads-overview__stat">
                            <div className="ads-overview__value ads-overview__value--pending">
                                {pendingCount}
                            </div>
                            <div className="ads-overview__label">Pending</div>
                        </div>

                        <div className="ads-overview__stat">
                            <div className="ads-overview__value ads-overview__value--revenue">
                                ₹{totalValue.toLocaleString()}
                            </div>
                            <div className="ads-overview__label">Value</div>
                        </div>
                    </div>

                    <Link to="/add-ads" className="ads-overview__cta">
                        <Button size="large" type="primary" icon={<PlusOutlined />}>
                            Create Ad
                        </Button>
                    </Link>
                </section>

                {/* ── TOOLBAR ── */}
                <section className="ads-toolbar">
                    <div className="ads-toolbar__left">
                        <div className="ads-toolbar__title-wrap">
                            <Text className="ads-toolbar__title">My Ads</Text>
                            <Text className="ads-toolbar__subtitle">
                                Manage all your ad listings in one place
                            </Text>
                        </div>
                    </div>

                    <div className="ads-toolbar__right">
                        {isMobileFilter ? (
                            <Select
                                value={filter}
                                onChange={(value) => setFilter(value)}
                                options={filterOptions}
                                className="ads-filters__select"
                                classNames={{ popup: { root: "ads-filters__dropdown" } }}
                                size="middle"
                            />
                        ) : (
                            <div className="ads-filters">
                                <Space wrap size={[10, 10]}>
                                    <Button
                                        type={filter === "all" ? "primary" : "default"}
                                        onClick={() => setFilter("all")}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        type={filter === "approved" ? "primary" : "default"}
                                        onClick={() => setFilter("approved")}
                                    >
                                        Approved
                                    </Button>
                                    <Button
                                        type={filter === "rejected" ? "primary" : "default"}
                                        onClick={() => setFilter("rejected")}
                                    >
                                        Rejected
                                    </Button>
                                    <Button
                                        type={filter === "active" ? "primary" : "default"}
                                        onClick={() => setFilter("active")}
                                    >
                                        Active
                                    </Button>
                                    <Button
                                        type={filter === "inactive" ? "primary" : "default"}
                                        onClick={() => setFilter("inactive")}
                                    >
                                        Inactive
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </div>
                </section>


                {/* ── EMPTY ── */}
                {ads.length === 0 ? (
                    <div className="ads-empty">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <Text strong className="ads-empty__title">
                                        No Ads yet
                                    </Text>
                                    <Paragraph className="ads-empty__text">
                                        Create your first ad listing to get started
                                    </Paragraph>
                                </div>
                            }
                        >
                            <Link to="/add-ads">
                                <Button type="primary" size="large">
                                    Create New Ad
                                </Button>
                            </Link>
                        </Empty>
                    </div>
                ) : (
                    <section className="ads-grid">
                        {ads.map((ad) => (
                            <article className="adss-card" key={ad._id}>
                                {/* ── Media ── */}
                                <div className="adss-card__media">
                                    <img
                                        src={ad.thumbnail || "/no-image.png"}
                                        alt={ad.title}
                                        className="adss-card__image"
                                    />

                                    <div className="adss-card__badges">
                                        <Tag
                                            className="adss-card__badge"
                                            style={{
                                                background: getStatusConfig(ad.status).bg,
                                                color: getStatusConfig(ad.status).color,
                                                borderColor: "transparent",
                                            }}
                                        >
                                            {getStatusConfig(ad.status).label}
                                        </Tag>
                                        <Tag
                                            className="adss-card__badge"
                                            style={{
                                                background: getApprovalConfig(ad.approvalStatus).bg,
                                                color: getApprovalConfig(ad.approvalStatus).color,
                                                borderColor: "transparent",
                                            }}
                                        >
                                            {getApprovalConfig(ad.approvalStatus).label}
                                        </Tag>
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(ad._id, ad.title)}
                                            className="adss-card__delete-btn"
                                            danger
                                        />
                                    </div>
                                </div>

                                {/* ── Body ── */}
                                <div className="adss-card__body">
                                    <Text className="adss-card__title" title={ad.title}>
                                        {ad.title}
                                    </Text>

                                    <div className="adss-card__price">
                                        <Text className="adss-card__price-value">
                                            ₹{Number(ad.pricing?.price || 0).toLocaleString("en-IN")}
                                        </Text>
                                    </div>

                                    <div className="adss-card__meta">
                                        <div className="adss-card__meta-item">
                                            <EnvironmentOutlined className="adss-card__meta-icon" />
                                            <span>{ad.location?.city || "N/A"}</span>
                                        </div>
                                    </div>

                                    {/* ── Actions ── */}
                                    <div className="adss-card__footer">
                                        <div className="adss-card__actions">
                                            <Button
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => navigate(`/edit-ads/${ad._id}`)}
                                                className="ads-btn ads-btn--secondary"
                                            >
                                                Edit
                                            </Button>

                                            <Popconfirm
                                                title={`Mark as ${ad.status === "active" ? "Inactive" : "Active"}`}
                                                description="This will update visibility immediately."
                                                onConfirm={() => handleStatusToggle(ad._id, ad.status)}
                                                okText="Confirm"
                                                cancelText="Cancel"
                                                okButtonProps={{ danger: ad.status === "active" }}
                                            >
                                                <Button
                                                    size="small"
                                                    loading={actionLoadingId === ad._id}
                                                    className={`ads-btn ${ad.status === "active"
                                                        ? "ads-btn--active"
                                                        : "ads-btn--inactive"
                                                        }`}
                                                >
                                                    {ad.status === "active" ? "Deactivate" : "Activate"}
                                                </Button>
                                            </Popconfirm>

                                            <Link
                                                to={`/ads/${ad.slug || ad._id}`}
                                                className="adss-card__view-link"
                                            >
                                                <Button
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    className="ads-btn ads-btn--primary"
                                                >
                                                    View
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </section>
                )}

                {/* ── PAGINATION ── */}
                {total > 0 && (
                    <div className="ads-pagination">
                        <Pagination
                            current={page}
                            total={total}
                            pageSize={15}
                            onChange={(p) => setPage(p)}
                            showSizeChanger={false}
                            responsive
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default MyAds;